import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, SyntheticEvent } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearMaterialPriceAndImages } from '../../redux/slices/curProductSlice';
import { fetchMaterialPriceAndImagesForCurProdct } from '../../services';
import fallbackImage from '../../assets/img/ImgFV.jpg';
import type { CursorPositionState, LoadedImagesState } from '../interfaces';
import { getMaterialImageIds, getMaterialMediaItems } from './imageForProductById.utils';

const useProductMediaGallery = () => {
  const dispatch = useAppDispatch();
  const selectedMaterial = useAppSelector((state) => state.curProduct.selectedMaterial);
  const productId = useAppSelector((state) => state.curProduct.productId?.id);
  const rawImages = useAppSelector((state) => state.curProduct.materialPriceAndImg);
  const imgStatus = useAppSelector((state) => state.curProduct.imgStatus);
  const priceAndImages = useMemo(() => rawImages ?? [], [rawImages]);
  const hasProductImages = useMemo(
    () => priceAndImages.some((item) => (item.productImages?.length ?? 0) > 0),
    [priceAndImages]
  );
  const [loadedImages, setLoadedImages] = useState<LoadedImagesState>({});
  const [zoomedImageId, setZoomedImageId] = useState<string | null>(null);
  const [cursorPosition, setCursorPosition] = useState<CursorPositionState>({});
  const lastMaterialRequestRef = useRef<string | null>(null);
  const materialMediaItems = useMemo(
    () => getMaterialMediaItems(priceAndImages, selectedMaterial),
    [priceAndImages, selectedMaterial]
  );

  useEffect(() => {
    if (!selectedMaterial || !productId) return;

    const requestKey = `${productId}:${selectedMaterial}`;
    const hasCurrentMaterialPreview = priceAndImages.some(
      (item) => item.materialName === selectedMaterial && item.productImages?.length
    );

    if (hasCurrentMaterialPreview || lastMaterialRequestRef.current === requestKey) return;

    lastMaterialRequestRef.current = requestKey;
    dispatch(clearMaterialPriceAndImages());
    dispatch(fetchMaterialPriceAndImagesForCurProdct({ productId, selectedMaterial }));
  }, [dispatch, productId, selectedMaterial, priceAndImages]);

  useEffect(() => {
    const nextImageIds = getMaterialImageIds(priceAndImages);

    setLoadedImages((prev) => {
      const newState = { ...prev };
      let changed = false;

      for (const id of nextImageIds) {
        if (!newState[id]) {
          newState[id] = { isLoaded: true, showImage: true };
          changed = true;
        }
      }

      return changed ? newState : prev;
    });
  }, [priceAndImages]);

  useEffect(() => {
    setZoomedImageId(null);
  }, [productId, selectedMaterial]);

  const handleImageLoad = (id: string) => {
    setLoadedImages((prev) => ({
      ...prev,
      [id]: { isLoaded: true, showImage: true },
    }));
  };

  const handleImageError = (event: SyntheticEvent<HTMLImageElement>, id: string) => {
    setLoadedImages((prev) => ({
      ...prev,
      [id]: { isLoaded: true, showImage: true },
    }));
    event.currentTarget.src = fallbackImage;
    event.currentTarget.classList.add('fallback-image');
  };

  const handlePointerMove = (event: ReactMouseEvent<HTMLDivElement>, imageId: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    setCursorPosition((prev) => ({
      ...prev,
      [imageId]: {
        x: Math.min(100, Math.max(0, x)),
        y: Math.min(100, Math.max(0, y)),
      },
    }));
  };

  const handleImageClick = (imageId: string) => {
    setZoomedImageId((prev) => (prev === imageId ? null : imageId));
  };

  return {
    imgStatus,
    hasProductImages,
    hasMaterialRows: priceAndImages.length > 0,
    materialMediaItems,
    loadedImages,
    cursorPosition,
    zoomedImageId,
    handleImageLoad,
    handleImageError,
    handlePointerMove,
    handleImageClick,
  };
};

export default useProductMediaGallery;
